import { Box, Heading, Image, Stack, Text } from "@chakra-ui/react";
import { CSSProperties } from "react";

import { TAppRoutes } from "hooks/useRouter";
import CustomLink from "components/Chakra/CustomLink";
import { IconArrowRight } from "src/components/Chakra/Icons";
import { useLoginModal } from "modals/useLoginModal";
import { useTracking } from "contexts/Tracking";

type ThemeProps = {
  color: string;
  bg: string;
  direction: "column" | "column-reverse";
};

export type CardType =
  | "ORANGE"
  | "GREY_DARK"
  | "GREY_LIGHT"
  | "GRAY_LIGHT_REVERT"
  | "GRAY_DARK_REVERT"
  | "ORANGE_REVERT"
  | "IMAGE_BACKGROUND_BLACK"
  | "IMAGE_BACKGROUND_WHITE"
  | "IMAGE_BACKGROUND_DARK_GRAY";

export type ImageProps = {
  src: string;
  style: CSSProperties;
};

type CardVariantProps = {
  title: string;
  subTitle: string;
  type: CardType;
  htmlTagType?: "BUTTON" | "LINK";
  to?: TAppRoutes;
  href?: string;
  isDisabled?: boolean;
  bgImage?: {
    scr: string;
    objectFit?: string;
    filter?: string;
  };
  image?: ImageProps;
  bottomText?: string;
  trackingElab?: string;
};

const getTypeTheme = (type: CardType): ThemeProps => {
  switch (type) {
    case "ORANGE":
    case "ORANGE_REVERT":
      return {
        color: "ud-white",
        bg: "linear-gradient(86deg, #E74C3C 0%, #E98540 100%)",
        direction: type === "ORANGE_REVERT" ? "column-reverse" : "column",
      };
    case "GREY_DARK":
    case "GRAY_DARK_REVERT":
      return {
        color: "ud-white",
        bg: "ud-grey-dark",
        direction: type === "GRAY_DARK_REVERT" ? "column-reverse" : "column",
      };
    case "GREY_LIGHT":
    case "GRAY_LIGHT_REVERT":
      return {
        color: "ud-black",
        bg: "ud-grey-light",
        direction: type === "GRAY_LIGHT_REVERT" ? "column-reverse" : "column",
      };
    case "IMAGE_BACKGROUND_WHITE":
      return {
        color: "ud-white",
        bg: "none",
        direction: "column",
      };
    case "IMAGE_BACKGROUND_DARK_GRAY":
      return {
        color: "ud-white",
        bg: "none",
        direction: "column",
      };
    case "IMAGE_BACKGROUND_BLACK":
      return {
        color: "ud-black-900",
        bg: "none",
        direction: "column",
      };
    default: {
      return {
        color: "ud-white",
        bg: "ud-white",
        direction: "column",
      };
    }
  }
};

const CardVariant = ({
  title,
  subTitle,
  type,
  bgImage,
  image,
  href,
  to,
  htmlTagType = "LINK",
  isDisabled = false,
  bottomText,
  trackingElab,
}: CardVariantProps) => {
  const theme: ThemeProps = getTypeTheme(type);
  const { openModal: openLoginModal } = useLoginModal();

  const { trackEvent } = useTracking();

  return (
    <>
      {(type === "IMAGE_BACKGROUND_BLACK" ||
        type === "IMAGE_BACKGROUND_WHITE" ||
        type === "IMAGE_BACKGROUND_DARK_GRAY") &&
        bgImage?.scr && (
          <Box
            position="absolute"
            w="100%"
            h="100%"
            borderRadius="6px"
            overflow="hidden"
            backgroundColor={
              type === "IMAGE_BACKGROUND_DARK_GRAY"
                ? "ud-grey-dark"
                : "rgba(0, 0, 0, 0.5)"
            }
          >
            <Image
              src={bgImage.scr}
              alt={`Obrázek ${title}`}
              h="100%"
              w="100%"
              objectFit="cover"
              filter={bgImage.filter ?? "brightness(60%)"}
              objectPosition={bgImage.objectFit ?? "center"}
            />
          </Box>
        )}

      <CustomLink
        as={htmlTagType === "LINK" ? "a" : "button"}
        to={to}
        href={href}
        cursor={isDisabled ? "not-allowed" : "pointer"}
        target={
          htmlTagType === "LINK" && href && href?.length > 0
            ? "_blank"
            : "_self"
        }
        display="flex"
        h="inherit"
        flex={1}
        textAlign="left"
        p={{ base: "24px", md: "40px" }}
        bg={theme.bg}
        flexDirection="column"
        zIndex={2}
        onClick={() => {
          if (trackingElab) {
            trackEvent({
              "dl.eCat": "homepage",
              "dl.eAct": "bttn.clicked",
              "dl.eLab": trackingElab,
            });
          }
          if (htmlTagType === "BUTTON") {
            openLoginModal({});
          }
        }}
      >
        <Stack spacing={1} direction={theme.direction}>
          <Text
            fontSize={type.includes("REVERT") ? "14px" : "16px"}
            display="block"
            lineHeight="20px"
            fontWeight={400}
            color={theme.color}
            textAlign="left"
          >
            {subTitle}
          </Text>
          <Heading
            as="h3"
            fontSize={
              type.includes("REVERT")
                ? { base: "20px", md: "24px" }
                : { base: "24px", lg: "32px" }
            }
            lineHeight={
              type.includes("REVERT") ? "26px" : { base: "32px", lg: "36px" }
            }
            fontWeight="bold"
            color={theme.color}
          >
            {title}
          </Heading>
        </Stack>
        <IconArrowRight
          borderWidth="1px"
          borderRadius="full"
          borderColor={theme.color}
          fill={theme.color}
          position="absolute"
          bottom="20px"
          right="20px"
          cursor="pointer"
          boxSize="24px"
          zIndex={2}
        />
        {image?.src && (
          <Stack flex={1}>
            <Image src={image.src} alt={title} style={image.style} />
          </Stack>
        )}
        {bottomText && (
          <Text
            position="absolute"
            bottom="20px"
            color="ud-white"
            fontWeight={300}
            left={{ base: "24px", md: "40px" }}
          >
            {bottomText}
          </Text>
        )}
      </CustomLink>
    </>
  );
};

export default CardVariant;
